import type { MountainContext } from "../types/MountainContext.js";

export interface ValidationResult {
  readonly valid: boolean;
  readonly issues: readonly string[];
}

const isFiniteNumber = (value: number): boolean => Number.isFinite(value);
const isTimestamp = (value: string): boolean => Number.isFinite(Date.parse(value));

export class ContextValidator {
  validate(context: MountainContext): ValidationResult {
    const issues: string[] = [];

    if (!isTimestamp(context.timestamp)) {
      issues.push("context.timestamp is invalid");
    }

    if (context.gps) {
      if (!isFiniteNumber(context.gps.latitude) || context.gps.latitude < -90 || context.gps.latitude > 90) {
        issues.push("gps.latitude is outside valid range");
      }
      if (!isFiniteNumber(context.gps.longitude) || context.gps.longitude < -180 || context.gps.longitude > 180) {
        issues.push("gps.longitude is outside valid range");
      }
      if (!isFiniteNumber(context.gps.accuracyMeters) || context.gps.accuracyMeters < 0) {
        issues.push("gps.accuracyMeters must be a non-negative finite number");
      }
      if (context.gps.altitudeMeters !== null && !isFiniteNumber(context.gps.altitudeMeters)) {
        issues.push("gps.altitudeMeters must be finite or null");
      }
      if (!isTimestamp(context.gps.timestamp)) {
        issues.push("gps.timestamp is invalid");
      }
    }

    if (context.route) {
      if (!isFiniteNumber(context.route.distanceTravelledMeters) || context.route.distanceTravelledMeters < 0) {
        issues.push("route.distanceTravelledMeters must be non-negative");
      }
      if (!isFiniteNumber(context.route.distanceRemainingMeters) || context.route.distanceRemainingMeters < 0) {
        issues.push("route.distanceRemainingMeters must be non-negative");
      }
      if (!isFiniteNumber(context.route.progressPercent) || context.route.progressPercent < 0 || context.route.progressPercent > 100) {
        issues.push("route.progressPercent must be between 0 and 100");
      }
      if (context.route.routeDeviationMeters !== null && (!isFiniteNumber(context.route.routeDeviationMeters) || context.route.routeDeviationMeters < 0)) {
        issues.push("route.routeDeviationMeters must be non-negative or null");
      }
      if (context.route.estimatedTimeRemainingMinutes !== null && (!isFiniteNumber(context.route.estimatedTimeRemainingMinutes) || context.route.estimatedTimeRemainingMinutes < 0)) {
        issues.push("route.estimatedTimeRemainingMinutes must be non-negative or null");
      }
      if (!isTimestamp(context.route.timestamp)) {
        issues.push("route.timestamp is invalid");
      }
    }

    if (context.weather) {
      if (context.weather.temperatureCelsius !== null && !isFiniteNumber(context.weather.temperatureCelsius)) {
        issues.push("weather.temperatureCelsius must be finite or null");
      }
      if (context.weather.windSpeedKph !== null && (!isFiniteNumber(context.weather.windSpeedKph) || context.weather.windSpeedKph < 0)) {
        issues.push("weather.windSpeedKph must be non-negative or null");
      }
      if (context.weather.gustSpeedKph !== null && (!isFiniteNumber(context.weather.gustSpeedKph) || context.weather.gustSpeedKph < 0)) {
        issues.push("weather.gustSpeedKph must be non-negative or null");
      }
      if (context.weather.precipitationMm !== null && (!isFiniteNumber(context.weather.precipitationMm) || context.weather.precipitationMm < 0)) {
        issues.push("weather.precipitationMm must be non-negative or null");
      }
      if (context.weather.precipitationProbabilityPercent !== null && (!isFiniteNumber(context.weather.precipitationProbabilityPercent) || context.weather.precipitationProbabilityPercent < 0 || context.weather.precipitationProbabilityPercent > 100)) {
        issues.push("weather.precipitationProbabilityPercent must be between 0 and 100 or null");
      }
      if (context.weather.visibilityMeters !== null && (!isFiniteNumber(context.weather.visibilityMeters) || context.weather.visibilityMeters < 0)) {
        issues.push("weather.visibilityMeters must be non-negative or null");
      }
      if (context.weather.uvIndex !== null && (!isFiniteNumber(context.weather.uvIndex) || context.weather.uvIndex < 0)) {
        issues.push("weather.uvIndex must be non-negative or null");
      }
      if (context.weather.next6HoursPrecipitationMm !== null && (!isFiniteNumber(context.weather.next6HoursPrecipitationMm) || context.weather.next6HoursPrecipitationMm < 0)) {
        issues.push("weather.next6HoursPrecipitationMm must be non-negative or null");
      }
      if (!isTimestamp(context.weather.timestamp)) {
        issues.push("weather.timestamp is invalid");
      }
    }

    for (const alert of context.alerts ?? []) {
      if (!alert.code.trim()) issues.push("alert.code must not be empty");
      if (!alert.message.trim()) issues.push("alert.message must not be empty");
      if (!isTimestamp(alert.timestamp)) issues.push("alert.timestamp is invalid");
    }

    return { valid: issues.length === 0, issues };
  }
}
